<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import LoaderAnimation from '../../components/LoaderAnimation.svelte';
import documentStore from '../../utils/documentStore';
import websocketsStore from '../../utils/websocketsStore';
import FilenameClip from './FilenameClip.svelte';

export let disableInteractions = false;

const percentFormatter = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0,
}); // Sicherstellen, dass Prozentwerte korrekt formatiert werden

let documents = [];
let stats = {
  activeChars: 0,
  charLimit: documentStore.MAX_TOTAL_CHARS,
  charUsagePercent: 0,
  isCharLimitExceeded: false,
  remainingChars: documentStore.MAX_TOTAL_CHARS,
  remainingTokens: documentStore.MAX_TOTAL_TOKENS,
  tokenLimit: documentStore.MAX_TOTAL_TOKENS,
};
let usagePercent = 0;
let usageState = 'normal';

documentStore.documents.subscribe((docs) => {
  documents = Object.values(docs);
});

documentStore.documentStats.subscribe((value) => {
  stats = value;
  usagePercent = Math.min(100, Math.round(value.charUsagePercent || 0));
  usageState = usagePercent >= 90 ? 'danger' : usagePercent >= 75 ? 'warning' : 'normal';
});

function getDocTooltip(doc) {
  if (!doc || !doc.n_chars) {
    return 'Kein Textlimit ermittelt.';
  }

  if (doc.isTooLarge) {
    return 'Dieses Dokument überschreitet das erlaubte Textlimit und kann nicht aktiviert werden.';
  }

  const limit = documentStore.MAX_TOTAL_CHARS || 0;
  const contribution = limit > 0 ? Math.round((doc.n_chars / limit) * 100) : 0;

  return `Dieses Dokument nutzt etwa ${percentFormatter.format(Math.min(contribution, 100))}% des Textlimits.`;
}

function handleToggle(doc) {
  const { reason } = documentStore.toggleActive(doc);
  if (reason === 'doc-too-large') {
    const docName = doc?.file?.name || 'Dokument';
    websocketsStore.currentMessage.error.set({
      message: `Dokument ${docName} kann nicht genutzt werden, weil es zu groß ist.`,
      type: 'error',
      persistent: true,
    });
    return;
  }
  if (reason === 'char-limit') {
    const docName = doc?.file?.name || 'Dokument';
    websocketsStore.currentMessage.warning.set({
      message: `Das Textlimit ist bereits erreicht. Bitte deaktivieren Sie andere Dokumente, um ${docName} zu aktivieren.`,
      type: 'warning',
      persistent: true,
    });
  }
}
</script>

<div class="document-bar">
    {#if documents.length > 0}
        <div
            class="text-amount-meter"
            class:locked={disableInteractions}
            data-tooltip="Textlimit: Summiert den erkannten Text aller angewählten Dokumente. Bilder, Grafiken und andere nicht-textuelle Inhalte werden nicht mitgezählt."
            aria-label="Textlimit: Summiert den erkannten Text aller angewählten Dokumente. Bilder, Grafiken und andere nicht-textuelle Inhalte werden nicht mitgezählt."
        >
            <div class="text-amount-header">
                <span class="text-amount-title">Textlimit</span>
                <span
                    class="text-amount-value"
                    class:warning={usageState === "warning"}
                    class:overused={stats.isCharLimitExceeded || usageState === "danger"}
                >
                    {percentFormatter.format(usagePercent)}% des Textlimits belegt
                </span>
            </div>
            <div
                class="text-amount-progress"
                class:warning={usageState === "warning"}
                class:danger={usageState === "danger"}
            >
                <div
                    class="text-amount-progress-bar"
                    class:warning={usageState === "warning"}
                    class:danger={usageState === "danger"}
                    style={`width: ${usagePercent}%`}
                ></div>
            </div>
            {#if stats.isCharLimitExceeded}
                <div class="text-amount-warning">
                    Textlimit überschritten – deaktivieren Sie Dokumente, um Nachrichten zu senden.
                </div>
            {:else if stats.remainingTokens === 0}
                <div class="text-amount-warning soft">
                    Textlimit erreicht – weitere Aktivierungen sind nicht möglich.
                </div>
            {/if}
        </div>
    {/if}
    <ul class="document-bar-list">
        {#each documents as doc}
            <li
                class="document"
                class:active={doc.is_active}
                class:inactive={!doc.is_active}
                class:too-large={doc.isTooLarge}
                class:locked={disableInteractions}
                data-tooltip={getDocTooltip(doc)}
            >
                <button
                    type="button"
                    class="document-toggle"
                    on:click={() => handleToggle(doc)}
                    aria-pressed={doc.is_active}
                    disabled={disableInteractions || doc.status === "error"}
                >
                    <span class="status-icon" aria-hidden="true">
                        {#if !doc.isTooLarge}
                            {#if doc.is_active}
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="9" stroke="#388e3c" stroke-width="2" fill="#e8f5e9"/>
                                    <path d="M6 10.5l2.5 2.5 5-5" stroke="#388e3c" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            {:else}
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="9" stroke="#9b9b9b" stroke-width="2" fill="#fff"/>
                                </svg>
                            {/if}
                        {/if}
                    </span>

                    {#if doc.status === "error"}
                        <span class="error-message">
                            Fehler beim Verarbeiten der Datei <em>{doc.file.name}</em>! <strong>Bitte versuchen Sie es erneut.</strong>
                        </span>
                    {:else}
                            <span class="filename-wrapper">
                                <FilenameClip filename={doc.file.name} />
                            </span>
                        <span class="file-size">
                            {#if doc.file && typeof doc.file.size === "number"}
                                {documentStore.formatFileSize(doc.file.size)}
                            {:else}
                                -
                            {/if}
                        </span>
                        {#if doc.status === "parsing"}
                            <LoaderAnimation/>
                        {/if}
                        {#if doc.isTooLarge}
                            <span class="too-large-message">
                                {doc.file?.name || 'Dokument'} wird nicht genutzt, weil es zu groß ist.
                            </span>
                        {/if}

                    {/if}
                </button>
                <button
                    type="button"
                    class="delete"
                    on:click|stopPropagation={() => documentStore.deleteFile(doc)}
                    aria-label={`Dokument ${doc.file?.name || ""} entfernen`}
                    disabled={disableInteractions}
                >
                    x
                </button>
            </li>
        {/each}
    </ul>
</div>

<style>
    .document-bar {
        width: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
    }

    .text-amount-meter {
        width: 100%;
        margin-bottom: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        position: relative;
        cursor: help;
    }

    .text-amount-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.95rem;
        font-weight: 600;
        color: #333;
        gap: 6px;
    }

    .text-amount-title {
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 0.82rem;
        color: #555;
    }

    .text-amount-value {
        font-size: 0.95rem;
        color: #1b5e20;
    }

    .text-amount-value.warning {
        color: #f57c00;
        font-weight: 600;
    }

    .text-amount-value.overused {
        color: #c62828;
        font-weight: 700;
    }

    .text-amount-progress {
        position: relative;
        width: 100%;
        height: 8px;
        border-radius: 999px;
        background: #e0e0e0;
        overflow: hidden;
    }

    .text-amount-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: linear-gradient(90deg, #66bb6a 0%, #2e7d32 100%);
        transition: width 0.3s ease;
    }

    .text-amount-meter::before,
    .text-amount-meter::after {
        position: absolute;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease;
    }

    .text-amount-meter::before {
        content: "";
        left: 12px;
        top: -8px;
        border: 6px solid transparent;
        border-top-color: rgba(33, 33, 33, 0.9);
        transform: translateY(-6px);
    }

    .text-amount-meter::after {
        content: attr(data-tooltip);
        left: 0;
        top: -10px;
        transform: translateY(-8px);
        background: rgba(33, 33, 33, 0.9);
        color: #fff;
        padding: 8px 10px;
        border-radius: 6px;
        font-size: 0.8rem;
        line-height: 1.2;
        max-width: 280px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        text-align: left;
        white-space: normal;
    }

    .text-amount-meter:hover::before,
    .text-amount-meter:hover::after,
    .text-amount-meter:focus-visible::before,
    .text-amount-meter:focus-visible::after {
        opacity: 1;
        transform: translateY(-12px);
    }

    .text-amount-progress.warning .text-amount-progress-bar,
    .text-amount-progress-bar.warning {
        background: linear-gradient(90deg, #f57c00 0%, #f57c00 100%);
    }

    .text-amount-progress.danger .text-amount-progress-bar,
    .text-amount-progress-bar.danger {
        background: linear-gradient(90deg, #c62828 0%, #c62828 100%);
    }

    .text-amount-warning {
        font-size: 0.85rem;
        color: #c62828;
        font-weight: 600;
    }

    .text-amount-warning.soft {
        color: #f57c00;
        font-weight: 500;
    }

    .document-bar-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-start;
        flex-direction: row;
        gap: 6px;
        max-height: 250px;
        overflow-y: auto;
        padding-right: 4px;
    }

    .document-bar .document {
        /*
        May not set position, filter, transform, ..., as this will make
        it the containing block for the tooltips and truncate them.
        See: https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Display/Containing_block#identifying_the_containing_block
        */
        display: flex;
        align-items: center;
        list-style: none;
        gap: 6px;
        padding: 3px 10px;
        margin-right: 3px;
        font-size: 0.8rem !important;
        background-color: #ffffff;
        color: #2c2c2c;
    }

    .document-bar .document:hover {
        background-color: #f2f2f2;
        border: #555555 solid 1px;
    }

    .document-bar .document::after {
        content: attr(data-tooltip);
        position: absolute;
        left: 0;
        top: 30px;
        transform: translateY(-6px);
        background: rgba(33, 33, 33, 0.9);
        color: #fff;
        padding: 6px 8px;
        border-radius: 6px;
        font-size: 0.7rem;
        line-height: 1.2;
        max-width: 260px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        text-align: left;
        white-space: normal;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease;
        z-index: 10;
    }

    .document-bar .document::before {
        content: "";
        position: absolute;
        left: 12px;
        top: 30px;
        border: 6px solid transparent;
        border-top-color: rgba(33, 33, 33, 0.9);
        transform: translateY(-6px);
        opacity: 0;
        transition: opacity 0.2s ease, transform 0.2s ease;
        pointer-events: none;
        z-index: 10;
    }

    .document-bar .document:hover::after,
    .document-bar .document:hover::before {
        opacity: 1;
        transform: translateY(-10px);
    }

    .document-bar .active {
        border: #555555 solid 1px;
    }

    .document-bar-list .inactive {
        color: #9b9b9b;
        border: #9b9b9b solid 1px;
    }

    .document-bar .document.too-large {
        border: #c62828 solid 1px;
        background-color: #ffebee;
        color: #2c2c2c;
    }

    .document-bar .document.too-large:hover {
        border-color: #b71c1c;
        background-color: #fde0dc;
    }

    .text-amount-meter.locked,
    .document-bar .document.locked {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .text-amount-meter.locked,
    .document-bar .document.locked:hover {
        background-color: #ffffff;
        border-color: #9b9b9b;
    }

    .document-bar .delete {
        background: transparent;
        border: none;
        cursor: pointer;
        margin-bottom: 3px;
        padding-left: 7px;
        font-weight: bold;
        font-size: larger;
        color: inherit;
    }

    .document-bar .delete:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    .document-toggle {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        flex: 1;
        gap: 6px;
        row-gap: 2px;
        justify-content: flex-start;
        background: transparent;
        border: none;
        padding: 0;
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: inherit;
        min-width: 0;
    }

    .document-toggle:disabled {
        cursor: default;
        opacity: 0.75;
    }

    .filename-wrapper {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        color: inherit;
        overflow: hidden;
    }

    .filename-wrapper :global(.filename-clip) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: inherit;
    }

    .error-message {
        color: #d32f2f;
    }

    .too-large-message {
        display: inline-block;
        flex-basis: 100%;
        color: #b71c1c;
        font-size: 0.7rem;
        line-height: 1.2;
        margin-top: 4px;
        padding-top: 4px;
        border-top: 1px dashed #ef9a9a;
    }

    .status-icon {
        margin-right: 7px;
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
    }

    .file-size {
        font-size: 0.7rem;
        color: #999;
        margin-left: 6px;
        margin-right: 6px;
    }
</style>
